import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, Trash2, GripVertical, Check, ChevronDown, Search } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { createChecklist, updateChecklist, Checklist, ChecklistTask } from '../../../services/checklists/checklistsService';
import { TranslatedText, SupportedLanguage, ALL_LANGUAGES, translateTexts } from '../../../services/recipes/recipesService';
import { TeamMember } from '../../../services/teamMembers/teamMembersService';
import { cn } from '../../../lib/utils';
import { secondaryText, smallTextSize, semiBoldText, primaryText, borderPrimary } from '../../../styling/stylingHelper';
import LanguageTabBar from '../../recipes/LanguageTabBar';
import { SortableList } from '../../common/SortableList';

// Generate a unique task ID
function generateTaskId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

interface SortableTaskRowProps {
  task: ChecklistTask;
  index: number;
  activeLanguage: SupportedLanguage;
  onTitleChange: (value: string) => void;
  onRemove: () => void;
  removeTitle: string;
}

const SortableTaskRow = ({ task, index, activeLanguage, onTitleChange, onRemove, removeTitle }: SortableTaskRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.taskId || `task-${index}`,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 p-3 rounded-lg border',
        borderPrimary,
        'bg-white dark:bg-zinc-900'
      )}
    >
      <button
        {...attributes}
        {...listeners}
        className="touch-none cursor-grab active:cursor-grabbing flex-shrink-0"
        aria-label="Drag to reorder"
        type="button"
      >
        <GripVertical className={cn('w-4 h-4', secondaryText)} />
      </button>
      <input
        type="text"
        value={task.titleTranslations?.[activeLanguage] || (activeLanguage === 'en' ? task.title : '') || ''}
        onChange={(e) => onTitleChange(e.target.value)}
        className={cn(
          'flex-1 px-2 py-1 rounded border',
          borderPrimary,
          'bg-transparent dark:bg-transparent',
          'text-zinc-900 dark:text-zinc-50',
          'focus:outline-none focus:ring-1 focus:ring-green-500 dark:focus:ring-green-500'
        )}
      />
      <button
        onClick={onRemove}
        className={cn(
          'p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20',
          'text-red-600 dark:text-red-400'
        )}
        title={removeTitle}
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

interface SaveChecklistModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  restaurantId: string;
  checklist?: Checklist;
  onSave: () => Promise<void>;
  teamMembers: TeamMember[];
}

export default function SaveChecklistModal({
  isOpen,
  onClose,
  mode,
  restaurantId,
  checklist,
  onSave,
  teamMembers,
}: SaveChecklistModalProps) {
  const { t, i18n } = useTranslation();
  const defaultLang = (['en', 'es', 'nl'].includes(i18n.language) ? i18n.language : 'en') as SupportedLanguage;

  const [nameTranslations, setNameTranslations] = useState<TranslatedText>({ en: '' });
  const [activeLanguage, setActiveLanguage] = useState<SupportedLanguage>(defaultLang);
  const [isTranslating, setIsTranslating] = useState(false);
  const [tasks, setTasks] = useState<ChecklistTask[]>([]);
  const [taskInput, setTaskInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [assignedTo, setAssignedTo] = useState<string[]>([]);
  const [clearsDaily, setClearsDaily] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && checklist) {
        setNameTranslations(checklist.nameTranslations || { en: checklist.name });
        setTasks(checklist.tasks || []);
        setAssignedTo(checklist.assignedTo || []);
        setClearsDaily(checklist.clearsDaily ?? false);
      } else {
        setNameTranslations({ en: '' });
        setTasks([]);
        setAssignedTo([]);
        setClearsDaily(false);
      }
      setActiveLanguage(defaultLang);
      setTaskInput('');
      setError(null);
      setSaving(false);
      setIsTranslating(false);
      setIsUserDropdownOpen(false);
      setUserSearchQuery('');
    }
  }, [isOpen, mode, checklist, defaultLang]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsUserDropdownOpen(false);
        setUserSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter team members by search query
  const filteredTeamMembers = teamMembers.filter((member) => {
    const displayName = member.name || member.email?.split('@')[0] || '';
    const email = member.email || '';
    const query = userSearchQuery.toLowerCase();
    return displayName.toLowerCase().includes(query) || email.toLowerCase().includes(query);
  });

  // Get display names of selected users
  const getSelectedUsersDisplay = () => {
    if (assignedTo.length === 0) return t('checklists.allUsers') || 'All users';
    if (assignedTo.length === 1) {
      const member = teamMembers.find(m => m.id === assignedTo[0]);
      return member?.name || member?.email?.split('@')[0] || '1 user';
    }
    return `${assignedTo.length} users selected`;
  };

  const toggleAssignedUser = (userId: string) => {
    setAssignedTo((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleAddTask = () => {
    if (taskInput.trim()) {
      const titleTranslations: TranslatedText = { en: '' };
      titleTranslations[activeLanguage] = taskInput.trim();
      if (activeLanguage !== 'en' && !titleTranslations.en) {
        titleTranslations.en = taskInput.trim();
      }
      const newTask: ChecklistTask = {
        taskId: generateTaskId(),
        title: titleTranslations.en,
        titleTranslations,
        pos: tasks.length,
        isActive: true,
        checked: false,
      };
      setTasks([...tasks, newTask]);
      setTaskInput('');
    }
  };

  const handleRemoveTask = (index: number) => {
    const updatedTasks = tasks
      .filter((_, i) => i !== index)
      .map((task, newIndex) => ({
        ...task,
        pos: newIndex,
      }));
    setTasks(updatedTasks);
  };

  const handleTaskTitleChange = (index: number, newTitle: string) => {
    const updatedTasks = [...tasks];
    const task = updatedTasks[index];
    const titleTranslations = { ...(task.titleTranslations || { en: task.title }) };
    titleTranslations[activeLanguage] = newTitle;
    if (activeLanguage === 'en') {
      updatedTasks[index] = { ...task, title: newTitle, titleTranslations };
    } else {
      updatedTasks[index] = { ...task, titleTranslations };
    }
    setTasks(updatedTasks);
  };

  const handleTasksReorder = (reordered: ChecklistTask[]) => {
    setTasks(reordered.map((task, index) => ({ ...task, pos: index })));
  };

  const handleTranslate = async () => {
    const currentName = nameTranslations[activeLanguage];
    if (!currentName?.trim()) return;

    setIsTranslating(true);
    try {
      const targetLangs = ALL_LANGUAGES.filter((l) => l !== activeLanguage);
      // Batch: checklist name + all task titles
      const texts: { key: string; value: string }[] = [
        { key: 'checklistName', value: currentName.trim() },
      ];
      tasks.forEach((task, i) => {
        const taskTitle = task.titleTranslations?.[activeLanguage] || task.title;
        if (taskTitle?.trim()) {
          texts.push({ key: `task_${i}`, value: taskTitle.trim() });
        }
      });

      const result = await translateTexts(texts, activeLanguage, targetLangs);

      // Apply checklist name translations
      const nameResult = result.translations['checklistName'];
      if (nameResult) {
        setNameTranslations((prev) => ({
          ...prev,
          ...Object.fromEntries(targetLangs.map((l) => [l, nameResult[l] || prev[l] || ''])),
        }));
      }

      // Apply task title translations
      const updatedTasks = tasks.map((task, i) => {
        const taskResult = result.translations[`task_${i}`];
        if (!taskResult) return task;
        const titleTranslations = { ...(task.titleTranslations || { en: task.title }) };
        targetLangs.forEach((l) => {
          if (taskResult[l]) titleTranslations[l] = taskResult[l];
        });
        return { ...task, titleTranslations };
      });
      setTasks(updatedTasks);
    } catch (err) {
      console.error('Translation failed:', err);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleSave = async () => {
    // Ensure .en is filled (fallback from active language)
    const finalNameTranslations = { ...nameTranslations };
    if (!finalNameTranslations.en?.trim() && finalNameTranslations[activeLanguage]?.trim()) {
      finalNameTranslations.en = finalNameTranslations[activeLanguage]!;
    }

    if (!finalNameTranslations.en?.trim()) {
      setError(t('checklists.nameRequired') || 'Checklist name is required');
      return;
    }

    if (tasks.length === 0) {
      setError(t('checklists.atLeastOneTask') || 'At least one task is required');
      return;
    }

    // Ensure all tasks have .en fallback
    const finalTasks = tasks.map((task) => {
      const tt = { ...(task.titleTranslations || { en: task.title }) };
      if (!tt.en?.trim() && tt[activeLanguage]?.trim()) {
        tt.en = tt[activeLanguage]!;
      }
      return { ...task, title: tt.en, titleTranslations: tt };
    });

    setSaving(true);
    setError(null);

    try {
      if (mode === 'create') {
        await createChecklist({
          restaurantId,
          name: finalNameTranslations.en.trim(),
          nameTranslations: finalNameTranslations,
          tasks: finalTasks,
          assignedTo: assignedTo.length > 0 ? assignedTo : undefined,
          clearsDaily,
        });
      } else if (checklist) {
        await updateChecklist({
          id: checklist.id,
          name: finalNameTranslations.en.trim(),
          nameTranslations: finalNameTranslations,
          tasks: finalTasks,
          assignedTo,
          clearsDaily,
        });
      }
      await onSave();
      onClose();
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Failed to save checklist');
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/50" onClick={onClose}>
      <div
        className="bg-card dark:bg-card-dark rounded-3xl shadow-2xl w-full max-w-2xl border border-zinc-200 dark:border-white/20 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-zinc-900 dark:text-zinc-50">
              {mode === 'create' ? t('checklists.createChecklist') : t('checklists.editChecklist')}
            </h2>
            <button
              onClick={onClose}
              className="flex-none p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Form Fields */}
          <div className="space-y-6">
            {/* Language Tabs */}
            <LanguageTabBar
              activeLanguage={activeLanguage}
              onChangeLanguage={setActiveLanguage}
              onTranslate={handleTranslate}
              isTranslating={isTranslating}
              hasContent={{
                en: !!(nameTranslations.en?.trim() || tasks.some((t) => t.titleTranslations?.en?.trim())),
                es: !!(nameTranslations.es?.trim() || tasks.some((t) => t.titleTranslations?.es?.trim())),
                nl: !!(nameTranslations.nl?.trim() || tasks.some((t) => t.titleTranslations?.nl?.trim())),
              }}
            />

            {/* Checklist Name */}
            <div>
              <label className={cn(smallTextSize, semiBoldText, primaryText, 'block mb-2')}>
                {t('checklists.name')}
              </label>
              <input
                type="text"
                value={nameTranslations[activeLanguage] || ''}
                onChange={(e) =>
                  setNameTranslations((prev) => ({ ...prev, [activeLanguage]: e.target.value }))
                }
                className={cn(
                  'w-full px-4 py-2 rounded-xl border',
                  borderPrimary,
                  'bg-white dark:bg-zinc-900',
                  'text-zinc-900 dark:text-zinc-50',
                  'focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-500'
                )}
                placeholder={t('checklists.namePlaceholder')}
              />
            </div>

            {/* Tasks */}
            <div>
              <label className={cn(smallTextSize, semiBoldText, primaryText, 'block mb-2')}>
                {t('checklists.tasks')}
              </label>

              {/* Add Task Input */}
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={taskInput}
                  onChange={(e) => setTaskInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTask();
                    }
                  }}
                  className={cn(
                    'flex-1 px-4 py-2 rounded-xl border',
                    borderPrimary,
                    'bg-white dark:bg-zinc-900',
                    'text-zinc-900 dark:text-zinc-50',
                    'focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-500'
                  )}
                  placeholder={t('checklists.taskTitlePlaceholder')}
                />
                <button
                  onClick={handleAddTask}
                  className={cn(
                    'px-4 py-2 rounded-xl',
                    'bg-darkGreenColor text-white dark:bg-darkGreenColor-dark dark:text-white',
                    'hover:bg-darkGreenColor/95 dark:hover:bg-darkGreenColor-dark/95',
                    'flex items-center gap-2'
                  )}
                >
                  <Plus className="w-4 h-4" />
                  <span>{t('checklists.addTask')}</span>
                </button>
              </div>

              {/* Tasks List */}
              {tasks.length === 0 ? (
                <p className={cn(smallTextSize, secondaryText, 'text-center py-4')}>
                  {t('checklists.noTasksYet')}
                </p>
              ) : (
                <SortableList
                  items={tasks}
                  getId={(task) => task.taskId || `task-${tasks.indexOf(task)}`}
                  onReorder={handleTasksReorder}
                  modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                >
                  <div className="space-y-2">
                    {tasks.map((task, index) => (
                      <SortableTaskRow
                        key={task.taskId}
                        task={task}
                        index={index}
                        activeLanguage={activeLanguage}
                        onTitleChange={(value) => handleTaskTitleChange(index, value)}
                        onRemove={() => handleRemoveTask(index)}
                        removeTitle={t('checklists.removeTask')}
                      />
                    ))}
                  </div>
                </SortableList>
              )}
            </div>

            {/* Assigned To */}
            <div>
              <label className={cn(smallTextSize, semiBoldText, primaryText, 'block mb-2')}>
                {t('checklists.assignedToLabel') || 'Assign To'}
                <span className={cn(smallTextSize, secondaryText, 'ml-1')}>
                  ({t('common.optional') || 'Optional'})
                </span>
              </label>
              {teamMembers.length === 0 ? (
                <p className={cn(smallTextSize, secondaryText, 'py-2')}>
                  {t('checklists.noTeamMembers') || 'No team members found'}
                </p>
              ) : (
                <div className="relative" ref={dropdownRef}>
                  {/* Dropdown trigger */}
                  <button
                    type="button"
                    onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                    className={cn(
                      'w-full px-4 py-2 rounded-xl border flex items-center justify-between',
                      borderPrimary,
                      'bg-white dark:bg-zinc-900',
                      'text-zinc-900 dark:text-zinc-50',
                      'focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-500'
                    )}
                  >
                    <span className={cn(smallTextSize, assignedTo.length === 0 && secondaryText)}>
                      {getSelectedUsersDisplay()}
                    </span>
                    <ChevronDown className={cn('w-4 h-4', secondaryText, isUserDropdownOpen && 'rotate-180 transition-transform')} />
                  </button>

                  {/* Dropdown menu */}
                  {isUserDropdownOpen && (
                    <div className="absolute z-50 bottom-full mb-1 w-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-lg max-h-64 overflow-hidden">
                      {/* Search input */}
                      <div className="p-2 border-b border-zinc-200 dark:border-zinc-700">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-textQuaternary-dark" />
                          <input
                            type="text"
                            value={userSearchQuery}
                            onChange={(e) => setUserSearchQuery(e.target.value)}
                            placeholder="Search users..."
                            className={cn(
                              'w-full pl-9 pr-3 py-1.5 rounded-lg border',
                              borderPrimary,
                              'bg-white dark:bg-zinc-800',
                              'text-zinc-900 dark:text-zinc-50',
                              'focus:outline-none focus:ring-1 focus:ring-green-500 dark:focus:ring-green-500',
                              smallTextSize
                            )}
                          />
                        </div>
                      </div>

                      {/* User list */}
                      <div className="max-h-48 overflow-y-auto">
                        {filteredTeamMembers.length === 0 ? (
                          <p className={cn(smallTextSize, secondaryText, 'p-3 text-center')}>
                            No users found
                          </p>
                        ) : (
                          filteredTeamMembers.map((member) => {
                            const isSelected = assignedTo.includes(member.id);
                            const displayName = member.name || member.email?.split('@')[0] || `User-${member.id.slice(-4)}`;
                            return (
                              <button
                                key={member.id}
                                type="button"
                                onClick={() => toggleAssignedUser(member.id)}
                                className={cn(
                                  'w-full px-3 py-2 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors',
                                  'text-left'
                                )}
                              >
                                <div className={cn(
                                  'w-5 h-5 rounded border flex items-center justify-center flex-shrink-0',
                                  isSelected
                                    ? 'bg-green-500 dark:bg-green-500 border-green-500 dark:border-green-500'
                                    : 'border-zinc-300 dark:border-zinc-600'
                                )}>
                                  {isSelected && <Check className="w-3 h-3 text-white dark:text-white" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={cn(smallTextSize, primaryText, 'truncate')}>
                                    {displayName}
                                  </p>
                                  {member.email && member.name && (
                                    <p className={cn('text-xs', secondaryText, 'truncate')}>
                                      {member.email}
                                    </p>
                                  )}
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>

                      {/* Clear selection */}
                      {assignedTo.length > 0 && (
                        <div className="p-2 border-t border-zinc-200 dark:border-zinc-700">
                          <button
                            type="button"
                            onClick={() => setAssignedTo([])}
                            className={cn(smallTextSize, 'text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 w-full text-center py-1')}
                          >
                            {t('checklists.clearSelection') || 'Clear selection'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {assignedTo.length === 0 && (
                <p className={cn(smallTextSize, secondaryText, 'mt-1')}>
                  {t('checklists.visibleToAll') || 'Not assigned to specific user'}
                </p>
              )}
            </div>
          </div>

          {/* Clears Daily Toggle */}
          <div className="mt-6">
            <div className="flex items-center justify-between">
              <div>
                <span className={cn(smallTextSize, semiBoldText, primaryText)}>
                  {t('checklists.clearsDaily')}
                </span>
                <p className={cn(smallTextSize, secondaryText, 'mt-0.5')}>
                  {t('checklists.clearsDailyDescription')}
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={clearsDaily}
                  onChange={(e) => setClearsDaily(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent/30 dark:peer-focus:ring-accent-dark/30 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-accent dark:peer-checked:bg-accent-dark"></div>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6 pt-6 border-t border-zinc-200 dark:border-zinc-700">
            <button
              onClick={onClose}
              disabled={saving}
              className={cn(
                'flex-1 px-6 py-3 rounded-xl',
                'border',
                borderPrimary,
                secondaryText,
                'hover:bg-gray-50 dark:hover:bg-gray-800',
                'disabled:opacity-50'
              )}
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className={cn(
                'flex-1 px-6 py-3 rounded-xl',
                'bg-darkGreenColor text-white dark:bg-darkGreenColor-dark dark:text-white',
                'hover:bg-darkGreenColor/95 dark:hover:bg-darkGreenColor-dark/95',
                'disabled:opacity-50'
              )}
            >
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

